// src/modules/app-views/_apps/appmulti/coupons/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import CouponsInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import couponsInitActionButton from './pages/Init/actions/actionButton';
import couponsInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CouponsDocument from './pages/Document';
import couponsDocumentActionButton from './pages/Document/actions/actionButton';
import couponsDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import couponsDocumentDecorateRequest from './pages/Document/decorators/request';
import couponsDocumentDecorateResponse from './pages/Document/decorators/response';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import { createdAtItem, updatedAtItem } from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import seedCouponsParameters from './seed';
import CouponsPreviewHeader from './pages/Init/components/CouponsPreviewHeader';
import CouponsPreviewActions from './pages/Init/components/CouponsPreviewActions';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import {
  couponCodeItem,
  couponDiscountItem,
  couponRemainingItem,
  couponExpirationItem,
} from './pages/Document/cardHeaderItems';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const MODULE_ID = 'coupons';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: seedCouponsParameters,

  migratory: {
    title: 'coupons_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <CouponsInit />
      </ListProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.COUPONS.PAGINATION,
      params: null,
      query: null,
      returnAsObject: false,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: null, paramKey: null, query: null },

    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['createdAt'],
    },

    settings: null,

    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'coupon',
        header: props => <CouponsPreviewHeader {...props} />,
        body: null,
        actions: props => <CouponsPreviewActions {...props} />,
        footer: null,
      },
    },

    header: {
      visible: true,
      style: 'default',
      // GOLDEN CONTRACT: Init actions are refs WITHOUT () — never invoke here
      actions: ['filter', 'refresh', couponsInitActionButton, couponsInitMenuButton],
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['financial', 'discount_coupons'],
      },
    },
  },

  document: {
    highSecurity: false,

    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <CouponsDocument />
      </DocumentProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.COUPONS.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: {
      request: couponsDocumentDecorateRequest,
      response: couponsDocumentDecorateResponse,
    },

    routing: { endpoint: '/:_id', paramKey: '_id', query: null },

    pagination: null,
    table: null,

    settings: {
      initialTab: 'record',
      initialValues: {
        type: 'percentage',
        qty: 1,
      },
      actions: [
        'editRefresh',
        couponsDocumentActionButton(),
        couponsDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    header: {
      visible: true,
      style: 'card',
      avatar: { visible: true, placeholder: 'file-text' },
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['financial', 'discount_coupons'],
      },
      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_coupon" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn
            {...props}
            items={[couponCodeItem, couponDiscountItem, couponRemainingItem, couponExpirationItem]}
          />
        ),
        column2: props => <CardHeaderColumn {...props} items={[createdAtItem, updatedAtItem]} />,
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
